/**
 * Access Explainer — "why can (or can't) this person do X?"
 *
 * Pick a person (and optionally a project), and the resolver returns the
 * final verdict for every module WITH the reasoning trace: which template
 * applied, which exceptions changed what. This is the support tool — no
 * more guessing how the permission system behaves.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  LevelBadge, makeApi, sectionCard, inputStyle, primaryBtn, errorBanner,
} from './AccessControlShared';

const SOURCE_LABELS = {
  superadmin: { text: 'Superadmin — master key', color: '#e74c3c' },
  project: { text: 'Project hat (template assigned for this project)', color: '#2ecc71' },
  default: { text: 'Company default template', color: '#3498db' },
  none: { text: 'No template assigned', color: '#95a5a6' },
};

export const AccessExplainerView = ({ api, initialData = null }) => {
  const [users, setUsers] = useState(initialData?.users || []);
  const [projects, setProjects] = useState(initialData?.projects || []);
  const [userId, setUserId] = useState(initialData?.userId || '');
  const [projectId, setProjectId] = useState(initialData?.projectId || '');
  const [result, setResult] = useState(initialData?.result || null);
  const [loading, setLoading] = useState(!initialData);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(initialData?.expanded || {});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, projectsRes] = await Promise.all([
        api('GET', '/access-control/users'),
        api('GET', '/access-control/projects'),
      ]);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load pickers. ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!initialData) load();
  }, [initialData, load]);

  const resolve = async () => {
    if (!userId) return;
    try {
      setResolving(true);
      setError(null);
      const url = `/access-control/resolve?userId=${encodeURIComponent(userId)}${projectId ? `&projectId=${projectId}` : ''}`;
      const res = await api('GET', url);
      setResult(res.data);
      setExpanded({});
    } catch (e) {
      console.error(e);
      setError('Failed to resolve. ' + (e.message || ''));
    } finally {
      setResolving(false);
    }
  };

  const sourceMeta = result ? (SOURCE_LABELS[result.templateSource] || SOURCE_LABELS.none) : null;
  const moduleEntries = useMemo(
    () => result ? Object.entries(result.modules) : [],
    [result]
  );
  const visibleCount = moduleEntries.filter(([, m]) => m.level >= 1).length;

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>Loading…</div>;
  }

  return (
    <div>
      {errorBanner(error)}

      {/* Pickers */}
      <div style={{ ...sectionCard, padding: 16, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#495057' }}>Explain access for</span>
        <select style={{ ...inputStyle, minWidth: 240 }} value={userId} onChange={e => setUserId(e.target.value)}>
          <option value="">Choose a person…</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
        </select>
        <span style={{ fontSize: 13, color: '#868e96' }}>on</span>
        <select style={{ ...inputStyle, minWidth: 220 }} value={projectId} onChange={e => setProjectId(e.target.value)}>
          <option value="">the whole company (no project)</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.job_name}</option>)}
        </select>
        <button style={primaryBtn(!userId || resolving)} disabled={!userId || resolving} onClick={resolve}>
          {resolving ? 'Resolving…' : 'Explain'}
        </button>
      </div>

      {result && (
        <div style={{ ...sectionCard, overflow: 'hidden' }}>
          {/* Verdict header */}
          <div style={{
            padding: '14px 18px', borderBottom: '1px solid #e9ecef',
            display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
                {result.user?.displayName || result.userId}
                {result.projectId && <span style={{ color: '#868e96', fontWeight: 400 }}> · project #{result.projectId}</span>}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 2 }}>
                <span style={{
                  padding: '2px 10px', borderRadius: 10, fontWeight: 700, fontSize: 11.5,
                  backgroundColor: `${sourceMeta.color}15`, color: sourceMeta.color, border: `1px solid ${sourceMeta.color}40`,
                }}>
                  {sourceMeta.text}{result.template ? `: "${result.template.name}"` : ''}
                </span>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: '#6c757d' }}>
              Sees <strong>{visibleCount}</strong> of {moduleEntries.length} modules
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef', color: '#495057' }}>
                <th style={{ textAlign: 'left', padding: '8px 18px' }}>Module</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Verdict</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Extras</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Why</th>
              </tr>
            </thead>
            <tbody>
              {moduleEntries.map(([key, m], idx) => (
                <tr key={key} style={{
                  backgroundColor: m.level === 0 ? '#fafbfc' : idx % 2 === 0 ? '#fff' : '#fafbfc',
                  borderBottom: '1px solid #f1f3f5',
                }}>
                  <td style={{ padding: '9px 18px', fontWeight: 600, color: m.level === 0 ? '#adb5bd' : '#212529', width: 220 }}>
                    {m.name}
                  </td>
                  <td style={{ padding: '9px 12px', width: 110 }}>
                    <LevelBadge level={m.level} />
                  </td>
                  <td style={{ padding: '9px 12px', maxWidth: 260 }}>
                    {m.level >= 3 ? (
                      <span style={{ fontSize: 11.5, color: '#e74c3c', fontWeight: 600 }}>everything</span>
                    ) : m.granular.length > 0 ? (
                      <span style={{ fontSize: 11.5, color: '#1e7e34' }}>{m.granular.join(', ')}</span>
                    ) : (
                      <span style={{ fontSize: 11.5, color: '#ced4da' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <button
                      style={{ background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: 12, padding: 0 }}
                      onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      {expanded[key] ? 'hide' : `${m.sources.length} step${m.sources.length === 1 ? '' : 's'} ▸`}
                    </button>
                    {expanded[key] && (
                      <ol style={{ margin: '6px 0 2px', paddingLeft: 18, fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                        {m.sources.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!result && (
        <div style={{ padding: 40, textAlign: 'center', color: '#868e96', fontSize: 13.5 }}>
          Pick a person to see exactly what they can do — and why.
        </div>
      )}
    </div>
  );
};

const AccessExplainerTab = () => {
  const { instance, accounts } = useMsal();
  const api = useMemo(() => makeApi(instance, accounts), [instance, accounts]);
  return <AccessExplainerView api={api} />;
};

export default AccessExplainerTab;
